import { format, parseISO } from "date-fns"
import { groupBy } from "lodash"
import React, { useContext } from "react"
import { useQuery } from "react-query"
import { Link } from "react-router-dom"
import RevenueMappingRow from "src/main/Accounting/RevenueMappingRow"

import InlineNotice from "src/components/InlineNotice"
import Loader from "src/components/Loader"

import { queryRevenueMappings } from "src/api/Accounting"

import { AccountingContext } from "./AccountingView"

// A custom G/L code from the Manage G/L Codes page has no ERP account behind it, so it has no type to group under.
const groupByAccountType = (codeOptions) => {
  const untyped = codeOptions.filter((option) => !option.accountTypeLabel)
  const byType = groupBy(
    codeOptions.filter((option) => option.accountTypeLabel),
    (option) => option.accountTypeLabel
  )
  const groups = Object.entries(byType).sort(([a], [b]) => a.localeCompare(b))
  return { untyped, groups }
}

// The server sends the marina's local time; format its date part so the browser's timezone can't shift the day.
const formatImportedOn = (accountsSyncedAt) =>
  format(parseISO(accountsSyncedAt.slice(0, 10)), "MMM d, yyyy")

const RevenueMappingTab = () => {
  const { marinaSlug } = useContext(AccountingContext)
  const queryKey = ["accounting", "revenue-mappings", marinaSlug]

  const { data, error, isError, isLoading } = useQuery(
    queryKey,
    () => queryRevenueMappings({ marinaSlug }),
    { retry: false, refetchOnWindowFocus: false }
  )

  if (isLoading) {
    return (
      <div className="bg-white">
        <Loader name="revenue mappings" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="text-muted bg-white p-5 text-center">
        <h3 className="mb-5">{error.message}</h3>
      </div>
    )
  }

  const revenueMappings = data.data
  const { serviceName, codeOptions, errorCount } = revenueMappings
  const codeOptionGroups = groupByAccountType(codeOptions)

  return (
    <div className="flex flex-col gap-5 pb-8 pt-6">
      <div>
        <h4 className="mb-1 text-2xl font-semibold text-gray-900">
          Revenue Mapping
        </h4>
        <p className="text-gray-800">
          Choose the {serviceName} account for each item below. Every account
          also labels a line in your Accrual Accounting report.
        </p>
      </div>

      {/* Each row explains its own problem, so the banner only counts errors, and a warning alone shows no banner. */}
      {errorCount > 0 && (
        <InlineNotice
          variant="error"
          icon="fa-circle-exclamation"
          iconStyle="solid"
        >
          <span className="font-semibold">
            {errorCount === 1
              ? "1 account needs to be fixed."
              : `${errorCount} accounts need to be fixed.`}
          </span>
        </InlineNotice>
      )}

      {revenueMappings.sections.map((section) => (
        <section
          key={section.key}
          aria-labelledby={`revenue-mapping-section-${section.key}`}
          className="rounded border border-gray-200 bg-white"
        >
          <div className="border-b border-gray-100 px-5 py-4">
            <h4
              id={`revenue-mapping-section-${section.key}`}
              className="mb-0 font-semibold"
            >
              {section.title}
            </h4>
            <p className="mb-0 mt-1 text-sm text-gray-700">
              {section.description}
            </p>
          </div>
          {section.rows.map((row) => (
            <RevenueMappingRow
              key={row.categoryId}
              row={row}
              codeOptions={codeOptions}
              codeOptionGroups={codeOptionGroups}
              marinaSlug={marinaSlug}
              queryKey={queryKey}
            />
          ))}
        </section>
      ))}

      <p className="text-sm text-gray-700">
        Account types come from your {serviceName} chart of accounts.{" "}
        {revenueMappings.accountsSyncedAt ? (
          <>
            Last refreshed {formatImportedOn(revenueMappings.accountsSyncedAt)}.{" "}
            <Link to="/setup">Import again from the Setup tab</Link>.
          </>
        ) : (
          <>
            They haven&apos;t been imported yet.{" "}
            <Link to="/setup">Import them from the Setup tab</Link>.
          </>
        )}
      </p>
    </div>
  )
}

export default RevenueMappingTab
